/* eslint-disable jsx-a11y/label-has-associated-control */
// @flow

import * as React from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Link } from '@mui/material';

function LoginForm({ setErrorMessage, loginRequest }: any): React$Element<any> {
  const rememberedUser = localStorage.getItem('rememberedUser');
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [recaptchaReady, setRecaptchaReady] = React.useState(false);

  // Check if reCAPTCHA is ready
  React.useEffect(() => {
    if (executeRecaptcha) {
      setRecaptchaReady(true);
    } else {
      setRecaptchaReady(false);
    }
  }, [executeRecaptcha]);

  const handleReCaptchaVerify = React.useCallback(async () => {
    if (!executeRecaptcha) {
      return null;
    }

    try {
      // Add a small delay to ensure reCAPTCHA is fully loaded
      await new Promise((resolve) => setTimeout(resolve, 100));

      const token = await executeRecaptcha('login');

      if (!token) {
        throw new Error('reCAPTCHA token generation failed');
      }

      return token;
    } catch (error) {
      console.error('reCAPTCHA execution error:', error);
      return null;
    }
  }, [executeRecaptcha]);
  return (
    <Formik
      initialValues={{ username: rememberedUser || '', password: '', rememberMe: !!rememberedUser }}
      validate={(values) => {
        const errors = {};
        if (!values.username) {
          errors.username = 'Required';
        }
        if (!values.password) {
          errors.password = 'Required';
        } else if (values.password.length < 8) {
          errors.password = 'Password must be at least 8 characters';
        }
        return errors;
      }}
      onSubmit={async (values, { setSubmitting, setFieldError }) => {
        try {
          if (values.rememberMe) {
            localStorage.setItem('rememberedUser', values.username);
          } else {
            localStorage.removeItem('rememberedUser');
          }

          const recaptchaToken = await handleReCaptchaVerify();

          if (!recaptchaToken) {
            setFieldError('general', 'reCAPTCHA verification failed. Please try again.');
            setSubmitting(false);
            return;
          }

          // Instead of hashing the password on the client, we send it to the server
          // It will be protected in transit by HTTPS
          const loginData = {
            username: values.username,
            password: values.password, // Send plaintext password (protected by HTTPS)
            rememberMe: values.rememberMe,
            recaptchaToken: recaptchaToken,
          };

          loginRequest(loginData);
          setSubmitting(false);
        } catch (error) {
          console.error('Form submission error:', error);
          setFieldError('general', 'reCAPTCHA verification failed. Please try again.');
          setSubmitting(false);
        }
      }}
    >
      {({ errors, touched, isSubmitting }) => (
        <Form className="col-md-12 p-0">
          <div className="form-group">
            <label className="form-label d-block" htmlFor="username">
              Username:
            </label>
            <Field
              type="username"
              name="username"
              id="username"
              className={`form-control ${
                errors.username !== undefined && touched.username !== undefined ? 'is-invalid' : touched.username && ' is-valid '
              }`}
              placeholder="Enter Username"
            />
            <ErrorMessage name="username" render={(msg) => <div className="invalid-feedback">{msg}</div>} />
          </div>
          <div className="form-group">
            <label className="form-label d-block" htmlFor="password">
              Password:
            </label>
            <Field
              type="password"
              name="password"
              id="password"
              className={`form-control ${
                errors.password !== undefined && touched.password !== undefined ? 'is-invalid' : touched.password && ' is-valid '
              }`}
              placeholder="Enter password"
            />
            <ErrorMessage name="password" render={(msg) => <div className="invalid-feedback">{msg}</div>} />
          </div>
          <div className="form-group">
            <div className="custom-control custom-checkbox">
              <Field type="checkbox" name="rememberMe" id="rememberMe" className="custom-control-input" />
              <label className="custom-control-label" htmlFor="rememberMe">
                Remember Me
              </label>
            </div>
          </div>
          <div className="form-group">
            <Link href="/forgot-password" className="text-100 ">
              <small>Forgot Password?</small>
            </Link>
          </div>
          {errors.general && (
            <div className="form-group">
              <div className="alert alert-soft-danger">
                <div className="d-flex flex-wrap">
                  <div className="mr-8pt">
                    <i className="material-icons">report</i>
                  </div>
                  <div className="flex" style={{ minWidth: '180px' }}>
                    <small className="text-100">{errors.general}</small>
                  </div>
                </div>
              </div>
            </div>
          )}
          {setErrorMessage && (
            <div className="alert alert-soft-danger">
              <div className="d-flex flex-wrap">
                <div className="mr-8pt">
                  <i className="material-icons">report</i>
                </div>
                <div className="flex" style={{ minWidth: '180px' }}>
                  <small className="text-100">{setErrorMessage}</small>
                </div>
              </div>
            </div>
          )}
          <button type="submit" className="btn btn-primary" disabled={isSubmitting || !recaptchaReady}>
            {isSubmitting ? 'Verifying...' : 'Login'}
          </button>
          {!recaptchaReady && (
            <div className="mt-2">
              <small className="text-warning">
                <i className="material-icons" style={{ fontSize: '14px', verticalAlign: 'middle' }}>
                  hourglass_empty
                </i>{' '}
                Loading security verification...
              </small>
            </div>
          )}
          <div className="mt-2">
            <small className="text-muted">
              This site is protected by reCAPTCHA and the Google{' '}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </a>{' '}
              and{' '}
              <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">
                Terms of Service
              </a>{' '}
              apply.
            </small>
          </div>
        </Form>
      )}
    </Formik>
  );
}

export default LoginForm;
